'use client';

import { useEffect, useState } from 'react';

// CSS imports for react-pdf (safe to import at module level)
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Polyfill for URL.parse (not available in Safari and older browsers)
// pdfjs-dist uses this internally and throws if not available
if (typeof window !== 'undefined' && typeof URL.parse !== 'function') {
  URL.parse = function (url: string, base?: string) {
    try {
      return new URL(url, base);
    } catch {
      return null;
    }
  };
}

interface PdfComponents {
  Document: React.ComponentType<Record<string, unknown>>;
  Page: React.ComponentType<Record<string, unknown>>;
}

interface UseReactPdfResult {
  PdfComponents: PdfComponents | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook for SSR-safe react-pdf loading
 *
 * Dynamically imports react-pdf only on the client side to avoid
 * DOMMatrix errors during server-side rendering.
 *
 * @param enabled - Whether to load react-pdf (default: true)
 * @returns Object with PdfComponents, isLoading, and error states
 *
 * @example
 * ```tsx
 * const { PdfComponents, isLoading } = useReactPdf();
 *
 * if (isLoading || !PdfComponents) {
 *   return <Skeleton />;
 * }
 *
 * return (
 *   <PdfComponents.Document file={url}>
 *     <PdfComponents.Page pageNumber={1} />
 *   </PdfComponents.Document>
 * );
 * ```
 */
export function useReactPdf(enabled: boolean = true): UseReactPdfResult {
  const [PdfComponents, setPdfComponents] = useState<PdfComponents | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    // Already loaded
    if (PdfComponents) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect -- async dynamic import; setState is the effect's intent
    setIsLoading(true);
    setError(null);

    import('react-pdf')
      .then((reactPdf) => {
        // Configure PDF.js worker using jsDelivr CDN (syncs with npm, more reliable than unpkg)
        reactPdf.pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${reactPdf.pdfjs.version}/build/pdf.worker.min.mjs`;

        setPdfComponents({
          Document: reactPdf.Document,
          Page: reactPdf.Page,
        });
        setIsLoading(false);
      })
      .catch((err) => {
        setError(
          err instanceof Error ? err : new Error('Failed to load PDF viewer'),
        );
        setIsLoading(false);
      });
  }, [enabled, PdfComponents]);

  return { PdfComponents, isLoading, error };
}

/**
 * Type for PDF document load success callback
 */
export interface PdfDocumentLoadSuccess {
  numPages: number;
}
